/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast'; // Fixed import path

// Add this interface for Paystack
interface PaystackResponse {
	reference: string;
	status: string;
	trans: string;
	transaction: string;
	message: string;
}

// Add this to fix window.PaystackPop typings
declare global {
	interface Window {
		PaystackPop: {
			setup(config: any): {
				openIframe(): void;
			};
		};
	}
}

interface WorkshopPaymentSummaryProps {
	workshop: {
		_id: string;
		title: string;
		day: string;
		month: string;
		year: string;
		time: string;
		location: string;
		price: string;
		image?: string;
	};
	bookingData: {
		fullName: string;
		email: string;
		phone: string;
		location: {
			city: string;
			state: string;
			country: string;
		};
	};
	onBack: () => void;
	onPayment: (success: boolean, message?: string) => void;
}

export function WorkshopPaymentSummary({
	workshop,
	bookingData,
	onBack,
	onPayment,
}: WorkshopPaymentSummaryProps) {
	const [isProcessing, setIsProcessing] = React.useState(false);
	const { toast } = useToast();

	// For free workshops
	const isFreeWorkshop = workshop.price === 'Free';

	// Generate a unique reference number for this transaction - match the format in BookingFlow
	const generatePaymentReference = () => {
		return `WORKSHOP-${workshop._id}-${Date.now()}`;
	};

	const handleFreeRegistration = async () => {
		setIsProcessing(true);

		try {
			// For consistency with BookingFlow, set a 1 second timeout
			setTimeout(() => {
				onPayment(true);
				setIsProcessing(false);
			}, 1000);
		} catch (error) {
			console.error('Error processing free registration:', error);
			toast({
				title: 'Registration Failed',
				description:
					'There was a problem with your registration. Please try again.',
			});
			setIsProcessing(false);
			onPayment(false, 'Registration failed. Please try again.');
		}
	};

	const handlePaymentInitiation = () => {
		setIsProcessing(true);

		// If it's free, process without payment
		if (isFreeWorkshop) {
			handleFreeRegistration();
			return;
		}

		// Following the implementation from PaymentSummary.tsx
		// Ensure we're removing any prior class in case of multiple attempts
		document.body.classList.remove('paystack-active');
		// Then add class to body to help with paystack z-index handling
		document.body.classList.add('paystack-active');

		// Add small delay to ensure CSS changes are applied
		setTimeout(() => {
			// Create a unique reference
			const reference = generatePaymentReference();

			// Save booking data to localStorage to retrieve after payment
			localStorage.setItem(
				'yaap_pending_workshop',
				JSON.stringify({
					workshop,
					bookingData,
					reference,
				}),
			);

			// Extract the numeric part of the price
			const rawPrice = workshop.price.replace(/[^0-9]/g, '');
			const amount = parseInt(rawPrice);

			if (isNaN(amount)) {
				toast({
					title: 'Invalid Price Format',
					description: 'Could not process the workshop price.',
				});
				setIsProcessing(false);
				return;
			}

			// Initialize Paystack payment
			const handler = (window as any).PaystackPop.setup({
				key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
				email: bookingData.email,
				amount: amount * 100, // convert to kobo (smallest currency unit)
				currency: 'ZAR',
				ref: reference,
				metadata: {
					custom_fields: [
						{
							display_name: 'Full Name',
							variable_name: 'full_name',
							value: bookingData.fullName,
						},
						{
							display_name: 'Workshop',
							variable_name: 'workshop_title',
							value: workshop.title,
						},
					],
				},
				label: workshop.title,
				description: `Workshop on ${workshop.day} ${workshop.month} ${workshop.year}`,
				callback: function (response: any) {
					// Regular function, not async
					document.body.classList.remove('paystack-active');
					notifyPaymentSuccess(response.reference);
				},
				onClose: function () {
					// Regular function, not async
					document.body.classList.remove('paystack-active');
					setIsProcessing(false);
					toast({
						title: 'Payment Cancelled',
						description: 'You have cancelled the payment process.',
					});
				},
			});

			handler.openIframe();
		}, 100);
	};

	const notifyPaymentSuccess = async (reference: string) => {
		try {
			// Use the same baseurl pattern as BookingFlow
			const baseurl =
				process.env.NODE_ENV === 'production'
					? 'https://yaap.co.za/.netlify/functions'
					: '/.netlify/functions';

			const response = await fetch(`${baseurl}/verify-payment`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					reference,
					serviceId: workshop._id,
				}),
			});

			const result = await response.json();

			if (result.success) {
				toast({
					title: 'Payment Successful',
					description: 'Your workshop registration is confirmed!',
				});
				onPayment(true);
			} else {
				toast({
					title: 'Payment Verification Failed',
					description:
						result.message || 'There was a problem verifying your payment.',
				});
				onPayment(false, result.message || 'Payment verification failed');
			}
		} catch (error) {
			console.error('Payment verification error:', error);
			toast({
				title: 'Verification Error',
				description: 'There was a problem verifying your payment.',
			});
			onPayment(false, 'Payment verification failed');
		} finally {
			setIsProcessing(false);
		}
	};

	return (
		<div className='space-y-8'>
			<SheetHeader className='p-0'>
				<SheetTitle className='text-3xl'>Complete Registration</SheetTitle>
				<p className='text-sm text-gray-500'>
					Please review your workshop registration details
				</p>
			</SheetHeader>

			<div className='grid grid-cols-1 md:grid-cols-2 gap-10'>
				<div>
					<h3 className='text-lg font-medium mb-4'>Workshop Details</h3>

					<Card>
						<CardContent className='p-6 space-y-4'>
							<div>
								<h4 className='font-medium text-lg'>{workshop.title}</h4>
								<p className='text-sm text-gray-500'>
									{`${workshop.day} ${workshop.month} ${workshop.year}`} at{' '}
									{workshop.time}
								</p>
								<p className='text-sm text-gray-500'>{workshop.location}</p>
							</div>

							{workshop.image && (
								<div className='mt-4'>
									<img
										src={workshop.image}
										alt={workshop.title}
										className='w-full h-auto rounded-md object-cover'
										style={{ maxHeight: '200px' }}
									/>
								</div>
							)}
						</CardContent>
					</Card>

					<div className='mt-6'>
						<h3 className='text-lg font-medium mb-4'>Price</h3>
						<div className='text-3xl font-bold'>R{workshop.price}</div>
					</div>
				</div>

				<div>
					<h3 className='text-lg font-medium mb-4'>Attendee Information</h3>

					<Card>
						<CardContent className='p-6 space-y-4'>
							<div className='space-y-1'>
								<p className='font-medium'>{bookingData.fullName}</p>
								<p className='text-sm'>{bookingData.email}</p>
								<p className='text-sm'>{bookingData.phone}</p>
							</div>

							<div className='pt-4 border-t'>
								<p className='text-sm text-gray-500'>Address</p>
								<p className='text-sm'>
									{bookingData.location.city}, {bookingData.location.state},{' '}
									{bookingData.location.country}
								</p>
							</div>
						</CardContent>
					</Card>

					<div className='flex gap-4 mt-8'>
						<Button
							type='button'
							variant='outline'
							onClick={onBack}
							disabled={isProcessing}
							className='flex items-center'
						>
							<ArrowLeft className='mr-2 h-4 w-4' /> Back
						</Button>

						<Button
							onClick={handlePaymentInitiation}
							disabled={isProcessing}
							className='bg-[#fbb040] hover:bg-[#f8c880] flex-1'
						>
							{isProcessing
								? 'Processing...'
								: isFreeWorkshop
									? 'Complete Registration'
									: 'Proceed to Payment'}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}
